import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Countdown from './components/Countdown';
import BookingWizard from './components/BookingWizard';
import EventsSection from './components/EventsSection';
import BookingModify from './components/BookingModify';
import CookieBanner from './components/CookieBanner';
import { CookiePolicyPage } from './components/CookieBanner';
import EventDetailModal from './components/EventDetailModal';
import FAQSection from './components/FAQSection';
import Footer from './components/Footer';
import AdminPage from './components/AdminPage';
import TermsPage from './components/TermsPage';
import JimmysPage from './components/JimmysPage';
import SpecialEventsPage from './components/SpecialEventsPage';
import CapodannoPage from './components/CapodannoPage';
import { Toaster } from './components/ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = '393518892962';
const WHATSAPP_MSG = encodeURIComponent('Ciao! Vorrei informazioni sulle prossime serate de La Peschiera Club.');

function FAQPage() {
  return (
    <div className="App" style={{ background: 'var(--bg-primary)', minHeight: '100vh' }}>
      <div style={{ paddingTop: '52px' }}>
        <FAQSection />
      </div>
      <div className="text-center py-10">
        <a href="/" className="btn-primary inline-flex">TORNA AL SITO</a>
      </div>
      <Footer />
    </div>
  );
}

function BookingModifyPage() {
  return (
    <div className="App" style={{ background: 'var(--bg-primary)', minHeight: '100vh' }}>
      <div style={{ paddingTop: '52px' }}>
        <BookingModify />
      </div>
      <div className="text-center py-10">
        <a href="/" className="btn-primary inline-flex">TORNA AL SITO</a>
      </div>
      <Footer />
    </div>
  );
}

function MainSite() {
  const [events, setEvents]             = useState([]);
  const [eventsLoaded, setEventsLoaded] = useState(false);
  const [highReadability, setHighReadability] = useState(false);
  const [detailEvent, setDetailEvent]   = useState(null);

  useEffect(() => {
    // Clear residual hash to prevent auto-scroll on page load
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname);
    }
    axios.get(`${API}/events?club=peschiera`)
      .then(r => { setEvents(r.data); setEventsLoaded(true); })
      .catch(e => { console.error('Failed to load events', e); setEventsLoaded(true); });
  }, []);

  const toggleReadability = () => {
    setHighReadability(v => !v);
    document.documentElement.classList.toggle('high-readability');
  };

  const scrollToPrenota = (event) => {
    if (event) window.history.replaceState(null, '', `#prenota-${event.id}`);
    document.getElementById('prenota')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="App" data-testid="app-root">
      <Toaster position="top-right" />
      <Navbar highReadability={highReadability} onToggleReadability={toggleReadability} />

      {/* Ticker scrolling banner */}
      <div
        data-testid="ticker-banner"
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 49,
          background: 'linear-gradient(90deg, #b8860b, #c9a84c, #b8860b)',
          overflow: 'hidden', height: '32px', display: 'flex', alignItems: 'center',
        }}
      >
        <div style={{
          display: 'flex', whiteSpace: 'nowrap',
          animation: 'tickerScroll 22s linear infinite',
        }}>
          {[...Array(6)].map((_, i) => (
            <span key={i} style={{
              color: '#0a0a0f', fontFamily: 'Manrope, sans-serif',
              fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.08em',
              paddingRight: '80px', textTransform: 'uppercase',
            }}>
              ★ &nbsp; In Diretta tutti i Sabati dalle 21:30 su Prima Antenna (canale 14)
            </span>
          ))}
        </div>
      </div>

      <Hero />
      {!eventsLoaded ? (
        <div className="py-16 flex justify-center items-center" style={{ background: '#060610', minHeight: '180px' }}>
          <div className="flex gap-5">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="flex flex-col items-center gap-2">
                <div style={{ width: 76, height: 76, borderRadius: 14, background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.15)', animation: 'pulse 1.5s ease-in-out infinite', animationDelay: `${i*0.15}s` }} />
                <div style={{ width: 48, height: 10, borderRadius: 4, background: 'rgba(255,255,255,0.06)' }} />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <Countdown events={events} onEventDetail={setDetailEvent} />
      )}
      <BookingWizard events={events} onEventDetail={setDetailEvent} />
      <EventsSection events={events} onEventDetail={setDetailEvent} />
      <Footer />
      <CookieBanner />

      {/* Event Detail Modal */}
      {detailEvent && (
        <EventDetailModal
          event={detailEvent}
          onClose={() => setDetailEvent(null)}
          onBook={(ev) => scrollToPrenota(ev)}
        />
      )}

      {/* WhatsApp floating button */}
      <a
        href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MSG}`}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-btn"
        aria-label="Contattaci su WhatsApp"
        style={{
          position: 'fixed',
          bottom: '80px',
          right: '20px',
          zIndex: 9998,
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          background: '#25D366',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 20px rgba(37,211,102,0.5)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          textDecoration: 'none',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = 'scale(1.1)';
          e.currentTarget.style.boxShadow = '0 6px 30px rgba(37,211,102,0.7)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 4px 20px rgba(37,211,102,0.5)';
        }}
      >
        <svg viewBox="0 0 24 24" width="30" height="30" fill="white" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/cookie-policy" element={<CookiePolicyPage />} />
        <Route path="/termini" element={<TermsPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/jimmys" element={<JimmysPage />} />
        <Route path="/eventi-speciali" element={<SpecialEventsPage />} />
        <Route path="/capodanno" element={<CapodannoPage />} />
        <Route path="/modifica-prenotazione" element={<BookingModifyPage />} />
        <Route path="/*" element={<MainSite />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
